/**
 * Date utilities — compute actual YYYY-MM-DD dates from presets.
 *
 * Port of Python's calculate_date_range() from base/utils.py.
 * The backend also has this, but the frontend needs it to:
 *   1. Send proper date params in API calls
 *   2. Show the resolved date range in the UI
 *
 * IMPORTANT: All dates are resolved in Europe/Istanbul timezone to match
 * BigQuery queries which use the same timezone for date boundaries.
 */
import {
  format,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
} from 'date-fns';

const FMT = 'yyyy-MM-dd';

/** The canonical timezone used throughout the dashboard */
const DASHBOARD_TZ = 'Europe/Istanbul';

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Get "now" in the dashboard timezone (Europe/Istanbul).
 * This ensures that "today" is always the Istanbul date,
 * regardless of the user's browser timezone.
 */
function getNowInDashboardTz(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: DASHBOARD_TZ }));
}

/**
 * Resolve a date preset (e.g. "last_28_days") into actual start/end dates.
 * If start/end are already provided, uses those instead.
 *
 * All presets resolve relative to Europe/Istanbul timezone.
 */
export function resolveDateRange(
  preset: string,
  start?: string,
  end?: string,
): DateRange {
  // If explicit dates are provided and no preset, use them
  if (start && end && !preset) {
    return { startDate: start, endDate: end };
  }

  const today = getNowInDashboardTz();
  const todayStr = format(today, FMT);

  switch (preset) {
    case 'today':
      return { startDate: todayStr, endDate: todayStr };

    case 'yesterday': {
      const yesterday = format(subDays(today, 1), FMT);
      return { startDate: yesterday, endDate: yesterday };
    }

    case 'last_7_days':
      return { startDate: format(subDays(today, 6), FMT), endDate: todayStr };

    case 'last_14_days':
      return { startDate: format(subDays(today, 13), FMT), endDate: todayStr };

    case 'last_28_days':
      return { startDate: format(subDays(today, 27), FMT), endDate: todayStr };

    case 'last_30_days':
      return { startDate: format(subDays(today, 29), FMT), endDate: todayStr };

    case 'last_90_days':
      return { startDate: format(subDays(today, 89), FMT), endDate: todayStr };

    case 'this_month':
      return { startDate: format(startOfMonth(today), FMT), endDate: todayStr };

    case 'last_month': {
      const lastMonth = subMonths(today, 1);
      return {
        startDate: format(startOfMonth(lastMonth), FMT),
        endDate: format(endOfMonth(lastMonth), FMT),
      };
    }

    case 'this_year':
      return { startDate: format(startOfYear(today), FMT), endDate: todayStr };

    default:
      // Default to last 28 days
      return { startDate: format(subDays(today, 27), FMT), endDate: todayStr };
  }
}
